import React, { SetStateAction, useCallback, useState } from "react"
import { useQuery } from "@tanstack/react-query";

import { useForm } from "antd/lib/form/Form";
import { Avatar, Button, Col, Collapse, Form, Grid, List, Popconfirm, Row, Tooltip, Typography } from "antd"

import { AlunoService } from "../../../../../Services/Domain/Pessoa/AlunoService";
import { SelectComPesquisa } from "../../../../../Components/SelectComPesquisa";
import { DownOutlined, PlusOutlined, QuestionCircleTwoTone } from "@ant-design/icons";
import { CadastroDeAluno } from "../Componentes/CadastroDeAluno";
import { Ordenar } from "../../../../../Utils/Ordenacao";
import { Formatacoes } from "../../../../../Utils/Formatacao";
import { TypeAluno } from "../../../../../Models/Domain/Pessoa/Aluno";

const avatarAluno = require("../../../../../Assets/avatar-aluno.jpg")

export const NovasInscricoes: React.FC<{
    formStyle?: React.CSSProperties,
    alunosJaInscritos?: TypeAluno[],
    novosAlunosAPI: [ TypeAluno[], React.Dispatch<SetStateAction<TypeAluno[]>> ],
}> = (props) => {

    const tamanhoDaTela = Grid.useBreakpoint();

    interface IFormularioDeInscricao {
        aluno: TypeAluno
    }

    const [form] = useForm<IFormularioDeInscricao>()

    const alunosDisponiveis = useQuery({
        queryKey: [`listarAlunosDisponiveis`],
        queryFn: async () => {
            return new AlunoService().listar({ativo: true})
        },
        gcTime: 0,
        enabled: true
    })

    const [ alunosInscritos, setAlunosInscritos ] = props.novosAlunosAPI
    const [propriedadesDePaginacaoDaLista, setPropriedadesDePaginacaoDaLista] = useState<Partial<{page: number, pageSize: number}>>({})

    /**
     * Filtro para ocultar os alunos já inscritos
     */
    const ocultarAlunosJaInscritos = useCallback((item: TypeAluno): boolean => {
        if(props.alunosJaInscritos != undefined && props.alunosJaInscritos.find((a) => a.codigo == item.codigo) != null) return false;
        if(alunosInscritos.find((a) => a.codigo == item.codigo) != null) return false;

        return true;
    }, [alunosInscritos, props.alunosJaInscritos])

    const aoFinalizar = (valores: IFormularioDeInscricao) => {
        if(alunosDisponiveis.data?.data == undefined) return;

        const alunoSelecionado = alunosDisponiveis.data.data.find((a) => a.codigo == valores.aluno.codigo);
        if(alunoSelecionado == undefined) return;

        setAlunosInscritos((prev) => [...prev, alunoSelecionado]);
        form.resetFields();

        /* Garante que o usuário seja redirecionado à última página da lista sempre que um novo item seja adicionado */
        setPropriedadesDePaginacaoDaLista((prev) => ({...prev, page: Math.ceil( ( alunosInscritos.length + 1) / ( propriedadesDePaginacaoDaLista.pageSize ?? 1 ) ) }))
    }

    const aoRemoverAluno = (aluno: TypeAluno) => {
        if(aluno.codigo == undefined) return;
        setAlunosInscritos((prev) => prev.filter((a) => a.codigo != aluno.codigo))
    }

    return <React.Fragment>
        <Form<IFormularioDeInscricao> form={form} style={{ marginBottom: "-1em", ...props.formStyle }} layout="vertical" onFinish={aoFinalizar}
            validateTrigger="onSubmit"
        >

            <Row gutter={16}>
                <Col xs={24} lg={20}>
                    <Form.Item<IFormularioDeInscricao>
                        name={["aluno", "codigo"]}
                        label={<><Typography>Selecione o aluno <Tooltip placement="bottomLeft" title={"Os alunos já inscritos ou adicionados não aparecem na seleção"}><QuestionCircleTwoTone /> </Tooltip>  </Typography> </>}
                        rules={[{ required: true, message: "Por favor, selecione o aluno!" }]}
                    >
                        <SelectComPesquisa
                            style={{ width: "100%" }}
                            loading={alunosDisponiveis.isFetching}
                            allowClear
                            placeholder="Selecione o aluno"
                            options={
                                alunosDisponiveis.data?.data
                                .filter(ocultarAlunosJaInscritos)
                                .sort((a, b) => Ordenar.String(a.pessoa?.nome + (a.pessoa?.cpf ?? ""), b.pessoa?.nome + (b.pessoa?.cpf ?? "")))
                                .map((aluno) => {
                                return { 
                                    value: aluno.codigo,
                                    label: `${aluno.pessoa?.nome} (${aluno.pessoa?.cpf ? Formatacoes.formatarCPF(aluno.pessoa?.cpf) : "N/A"})`,
                                }
                            })}
                        />
                    </Form.Item>
                </Col>

                <Col xs={24} lg={4}>
                    <Form.Item label={tamanhoDaTela.lg ? <></> : undefined} style={{ width: "100%" }}>
                        <Button htmlType="submit" type="primary"
                            style={{ width: "100%" }}
                        >
                            Incluir
                        </Button>
                    </Form.Item>

                </Col>

            </Row>

        </Form>

        <Row style={{ marginBottom: "1em" }}>
            <Col xs={24}>
                <Collapse
                    accordion
                    bordered={false}
                    size="small"
                    ghost
                    expandIcon={(panelProps) => {
                        return <React.Fragment> 
                            <Button 
                                type="link"
                                style={{ padding: 0 }} 
                                icon={panelProps.isActive == undefined ? <></> : panelProps.isActive == false ? <PlusOutlined /> : <DownOutlined />}
                            >
                                 Cadastrar novo aluno 
                            </Button> 
                        </React.Fragment>
                    }}
                    items={[
                        {
                            key: '1',
                            children: <CadastroDeAluno 
                                privilegio="administrador"
                                permitirCadastroDeResponsavel
                                depoisDeFinalizar={() => alunosDisponiveis.refetch()} 
                                txtSalvar="Salvar aluno"
                                txtAtualizar="Atualizar aluno"
                            />
                        }
                    ]}
                >
                </Collapse>

            </Col>
        </Row>

        <List
            header={<div className="common-list-header">Novas inscrições</div>}
            bordered
            locale={{ emptyText: 'Nenhum aluno adicionado.' }}
            pagination={{
                position: "bottom",
                align: "center",
                defaultPageSize: 5,
                onChange: ((page, pageSize) => setPropriedadesDePaginacaoDaLista({ page, pageSize })),
                current: propriedadesDePaginacaoDaLista.page,
                pageSize: propriedadesDePaginacaoDaLista.pageSize,
                hideOnSinglePage: false,
                pageSizeOptions: [5, 10, 20, 50, 100]
            }}
            dataSource={alunosInscritos}
            renderItem={(aluno, index) => {
                return (<List.Item
                    className="common-list-item"
                    actions={[
                        <Popconfirm title={"Tem certeza que deseja remover este aluno?"} onConfirm={() => {
                            aoRemoverAluno(aluno)
                        }}>
                            <a key="list-loadmore-edit" > Remover </a>,
                        </Popconfirm>
                    ]}
                >
                    <List.Item.Meta
                        avatar={<Avatar src={avatarAluno} />}
                        title={<a href="#">{aluno.pessoa?.nome}</a>}
                        description={
                            <>
                                CPF: {aluno.pessoa?.cpf ?? "N/A"}.
                                Responsável: {aluno.responsaveis?.find((r) => r.principal == true)?.responsavel.pessoa?.nome}
                            </>
                        }
                    />
                </List.Item>
                )
            }}
        />

    </React.Fragment>
}

export default NovasInscricoes